import { getPageData } from "services/craft/craft"
import { getEmmaMeetingIdByLocale } from "services/sites"
import runMiddleware from "lib/runMiddleware"
import Layout from "components/layout/layout"
import agendaFilters from "config/agendaFilters"
import SessionShareMeta from "components/header/sessionShareMeta"
import { sessionPath, findPresentation } from "lib/sessionUrl"
import { emmaFetch } from "services/emma/server"

export default function ProgramGuide({
    site,
    page,
    layoutVariant,
    sessionId,
    presentationId,
    sessionTypes,
    user,
    sharedSession,
    sharedPresentation,
    shareUrl,
    shareImage,
}) {
    page.showPageHeader = true
    const facetsConfig = agendaFilters(site)

    const isLegacy = !page.singlesMatrix?.length
    if (isLegacy) {
        site.meeting.sessionTypeTerm = page.programGuideSessionTypeTerm
    }

    return (
        <>
            {/* Per-session Open Graph tags for deep links. Rendered after Layout so this title wins
                over the generic one from SeoMetaFields inside it. */}
            <Layout
                site={site}
                className="program__page"
                bg="#fff"
                slug="program-guide"
                page={page}
                label="Program"
                matrix={page.singlesMatrix}
                user={user}
                sessionTypes={sessionTypes}
                sessionId={sessionId}
                presentationId={presentationId}
                facetsConfig={facetsConfig}
                useLegacyContent={isLegacy}
                layoutVariant={layoutVariant}
            />
            <SessionShareMeta
                session={sharedSession}
                presentation={sharedPresentation}
                site={site}
                url={shareUrl}
                image={shareImage}
            />
        </>
    )
}

export const getServerSideProps = runMiddleware(async function (ctx) {
    const user = ctx.user

    const emmaMeetingId = getEmmaMeetingIdByLocale(ctx.locale)

    const isAlternate = ctx.params.slug?.[0] === "alt"
    const slug = isAlternate ? ctx.params.slug[1] : "program-guide"
    // On the alternate route the session id follows the page slug (/program-guide/alt/<slug>/<id>),
    // otherwise it is the whole path (/program-guide/<id>). `?sessionId=` is the superseded form,
    // still honored so links shared before the switch keep resolving — including for crawlers.
    const sessionIndex = isAlternate ? 2 : 0
    const sessionId = ctx.params.slug?.[sessionIndex] || ctx.query.sessionId || null
    // A presentation link adds one more segment after the session id (/program-guide/<id>/<presId>).
    // Only meaningful with a session id in the path; the legacy query form never carried one.
    const presentationId = (ctx.params.slug?.[sessionIndex] && ctx.params.slug?.[sessionIndex + 1]) || null

    const requestPromises = [
        getPageData({
            locale: ctx.locale,
            page: {
                slug: slug,
                type: "programGuide",
            },
            query: ctx.query,
        }),
        emmaFetch("/conferences/" + emmaMeetingId + "/categories/session-type", {
            cache: "no-store",
        }).then(response => {
            if (!response.ok) {
                console.error(`Emma API error: ${response.status} for locale ${ctx.locale}, meetingId ${emmaMeetingId}`)
                return { data: [] }
            }
            return response.json()
        }),
        // If we have a session id, fetch the shared session data from Emma
        // non-fatal: a missing or pulled session falls back to the generic guide tags rather than
        // breaking the page for everyone. A presentation link needs the full subtree so the
        // presentation can be named in the share tags; a plain session link only needs one level.
        sessionId
            ? emmaFetch(
                  "/conferences/" +
                      emmaMeetingId +
                      "/events/" +
                      sessionId +
                      (presentationId ? "?depth=all" : "?depth=1"),
                  {
                      next: { revalidate: 1800 },
                  },
              )
                  .then(response => (response.ok ? response.json() : null))
                  .then(json => json?.event || null)
                  .catch(() => null)
            : Promise.resolve(null),
    ]

    const data = await Promise.all(requestPromises)

    // if the page is disabled, temporarily redirect to the homepage
    if (!data[0].page) {
        return {
            redirect: {
                destination: "/",
                permanent: false,
            },
        }
    }

    // if the site is not found, redirect to the homepage
    if (!data[0].site) {
        return {
            redirect: {
                destination: "/",
                permanent: false,
            },
        }
    }

    // Null when the path names a presentation that is not in this session: the tags then describe
    // the session, and the client normalizes the URL back to the session link. Both are trimmed to
    // the fields the share tags read — the `depth=all` subtree fetched for a presentation link
    // would otherwise be serialized into the page props for nothing.
    const foundPresentation = findPresentation(data[2], presentationId)
    const sharedPresentation = foundPresentation
        ? { id: foundPresentation.id, title: foundPresentation.title, description: foundPresentation.description ?? null }
        : null
    const { children: _children, ...sharedSessionFields } = data[2] || {}
    const sharedSession = data[2] ? sharedSessionFields : null

    // og:url has to name the session and nothing else. Built from the request host so preview and
    // local deployments describe themselves rather than production, and from the canonical path
    // helper so filter/search params never reach the tag — one cached preview per session.
    const forwardedProto = ctx.req.headers["x-forwarded-proto"]
    const protocol = forwardedProto ? forwardedProto.split(",")[0].trim() : "http"
    const origin = ctx.req.headers.host ? protocol + "://" + ctx.req.headers.host : ""
    const shareUrl = sharedSession
        ? origin +
          sessionPath("/program-guide/[[...slug]]", ctx.params.slug, sessionId, sharedPresentation ? presentationId : null)
        : null

    return {
        props: {
            section: "programGuide",
            sharedSession: sharedSession,
            sharedPresentation: sharedPresentation,
            shareUrl: shareUrl,
            // No image by design. Sessions have no artwork, and the meeting's logo lockup is
            // 943x187 — under Facebook's 200px minimum height, so it is ignored there, and X's
            // summary card crops 1:1, which would show a sliver of the middle of the logo. A
            // text-only card carrying the session title and schedule beats a broken one. Wire a
            // generated per-session image (title/time/room on a branded canvas) in here to
            // upgrade, and pass imageIsSessionSpecific so it gets the large X card.
            shareImage: null,
            site: data[0].site,
            page: data[0].page,
            sessionTypes: data[1].data ?? [],
            sessionId: sessionId,
            presentationId: presentationId,
            locale: ctx.locale,
            pageStatus: data[0].page.userStatus,
            user: user,
        },
    }
})
